import type { AIConfirmationProfile } from '../../../../modules/ai-button';
import type { ConfirmationMessage } from '../../chat-panel/message/confirmation';
import type { LocalizedCopy } from '../../utils/localized-copy';
import type { ChatMessage } from '../ai-chat-messages';
import type { TargetModeProposalDomain } from './retrieval-mode';

export interface TargetModePermissionProposal {
  readonly input: string;
  readonly domain: TargetModeProposalDomain;
  readonly reason: string;
  readonly approvalId: string;
  readonly sessionId: string;
  readonly description: string;
  readonly copy: LocalizedCopy;
  readonly confirmationProfile: AIConfirmationProfile;
  readonly includesAgentMode: boolean;
  readonly includesTargetMode: boolean;
}

export interface GoalConfirmationPermission {
  readonly goalId: string;
  readonly blockerCode: string;
  readonly approvalId: string;
  readonly sessionId: string;
  readonly description: string;
  readonly copy: LocalizedCopy;
}

type PermissionHistoryFetch = (
  input: string,
  init?: RequestInit
) => Promise<Response>;

async function recordPermissionHistory(
  event: Record<string, unknown>,
  fetcher: PermissionHistoryFetch
): Promise<string> {
  const response = await fetcher('/api/ai/permission-history', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(event),
  });
  if (!response.ok) {
    throw new Error('Failed to persist permission history');
  }
  const result = (await response.json()) as { messageId?: string };
  if (!result.messageId) {
    throw new Error('Permission history message id missing');
  }
  return result.messageId;
}

export async function persistTargetModePermissionRequest(
  proposal: TargetModePermissionProposal,
  workspaceId: string,
  threadId: string | undefined,
  fetcher: PermissionHistoryFetch
): Promise<ChatMessage> {
  const kind = proposal.includesAgentMode ? 'agent_mode' : 'target_mode';
  const messageId = await recordPermissionHistory(
    {
      event: 'request',
      workspaceId,
      sessionId: proposal.sessionId,
      threadId,
      approvalId: proposal.approvalId,
      kind,
      domain: proposal.domain,
      input: proposal.input,
      description: proposal.description,
      confirmationCopy: proposal.copy,
      confirmationProfile: proposal.confirmationProfile,
      includesAgentMode: proposal.includesAgentMode,
      includesTargetMode: proposal.includesTargetMode,
    },
    fetcher
  );
  return {
    id: messageId,
    role: 'assistant',
    content: '',
    createdAt: new Date().toISOString(),
    messageType: 'tool_approval_request',
    toolData: {
      approvalId: proposal.approvalId,
      toolCall: {
        name: kind,
        arguments: {
          domain: proposal.domain,
          input: proposal.input,
          confirmationProfile: proposal.confirmationProfile,
          includesAgentMode: proposal.includesAgentMode,
          includesTargetMode: proposal.includesTargetMode,
        },
      },
      description: proposal.description,
      confirmationCopy: proposal.copy,
    },
  };
}

export async function persistTargetModePermissionResponse(
  proposal: TargetModePermissionProposal,
  approved: boolean,
  workspaceId: string,
  threadId: string | undefined,
  fetcher: PermissionHistoryFetch
): Promise<ChatMessage> {
  const kind = proposal.includesAgentMode ? 'agent_mode' : 'target_mode';
  const messageId = await recordPermissionHistory(
    {
      event: 'response',
      workspaceId,
      sessionId: proposal.sessionId,
      threadId,
      approvalId: proposal.approvalId,
      kind,
      approved,
      includesAgentMode: proposal.includesAgentMode,
      includesTargetMode: proposal.includesTargetMode,
    },
    fetcher
  );
  return {
    id: messageId,
    role: 'user',
    content: approved ? `Approved: ${kind}` : `Rejected: ${kind}`,
    createdAt: new Date().toISOString(),
    messageType: 'tool_approval_response',
    toolData: {
      approvalId: proposal.approvalId,
      approved,
      includesAgentMode: proposal.includesAgentMode,
      includesTargetMode: proposal.includesTargetMode,
    },
  };
}

export async function persistGoalConfirmationRequest(
  permission: GoalConfirmationPermission,
  workspaceId: string,
  threadId: string | undefined,
  fetcher: PermissionHistoryFetch
): Promise<ChatMessage> {
  const messageId = await recordPermissionHistory(
    {
      event: 'request',
      workspaceId,
      sessionId: permission.sessionId,
      threadId,
      approvalId: permission.approvalId,
      kind: 'agent_goal',
      goalId: permission.goalId,
      blockerCode: permission.blockerCode,
      description: permission.description,
      confirmationCopy: permission.copy,
    },
    fetcher
  );
  return {
    id: messageId,
    role: 'assistant',
    content: '',
    createdAt: new Date().toISOString(),
    messageType: 'tool_approval_request',
    toolData: {
      approvalId: permission.approvalId,
      toolCall: {
        name: 'agent_goal',
        arguments: {
          goalId: permission.goalId,
          blockerCode: permission.blockerCode,
        },
      },
      description: permission.description,
      confirmationCopy: permission.copy,
    },
  };
}

export async function persistGoalConfirmationResponse(
  permission: GoalConfirmationPermission,
  approved: boolean,
  workspaceId: string,
  threadId: string | undefined,
  fetcher: PermissionHistoryFetch
): Promise<ChatMessage> {
  const messageId = await recordPermissionHistory(
    {
      event: 'response',
      workspaceId,
      sessionId: permission.sessionId,
      threadId,
      approvalId: permission.approvalId,
      kind: 'agent_goal',
      approved,
    },
    fetcher
  );
  return {
    id: messageId,
    role: 'user',
    content: approved ? 'Approved: agent_goal' : 'Rejected: agent_goal',
    createdAt: new Date().toISOString(),
    messageType: 'tool_approval_response',
    toolData: {
      approvalId: permission.approvalId,
      approved,
    },
  };
}

export function restoreTargetModePermissionProposal(
  confirmation: ConfirmationMessage,
  sessionId: string | undefined
): TargetModePermissionProposal | null {
  const domain = confirmation.metadata?.domain;
  const input = confirmation.metadata?.input;
  const confirmationProfile = confirmation.metadata?.confirmationProfile;
  const includesAgentMode =
    confirmation.metadata?.includesAgentMode === true ||
    confirmation.toolName === 'agent_mode';
  const includesTargetMode =
    confirmation.metadata?.includesTargetMode === true ||
    confirmation.toolName === 'target_mode';
  if (
    (domain !== 'custom_app' && domain !== 'automation') ||
    typeof input !== 'string' ||
    !input.trim() ||
    !sessionId ||
    !confirmation.copy ||
    (!includesAgentMode && !includesTargetMode) ||
    (confirmationProfile !== 'confirm_each' &&
      confirmationProfile !== 'policy' &&
      confirmationProfile !== 'autonomous')
  ) {
    return null;
  }
  return {
    input,
    domain,
    reason: '',
    approvalId: confirmation.id,
    sessionId,
    description: confirmation.description,
    copy: confirmation.copy,
    confirmationProfile,
    includesAgentMode,
    includesTargetMode,
  };
}
